import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { useAuth } from "../../../providers/AuthProvider";
import { ApiError, getValidationErrors } from "../../../services/apiClient";
import {
  listControlPoints,
  type ControlPoint,
} from "../../shared/api/controlPoints";
import {
  createLot,
  archiveLot,
  getLot,
  updateLot,
  type CreateLotPayload,
  type Lot,
  type LotTitleType,
  type LotSegmentInput,
  type UpdateLotPayload,
} from "../api/lots";
import type { Workspace } from "../../workspaces/api/workspaces";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";

import { Archive, ArrowLeft, ArrowsLeftRight, CheckCircle, Clock, FileText, FloppyDisk, Gift, Plus, Sparkle, TrashSimple, Wallet } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import {
  DEFAULT_TITLE_TYPE,
  TITLE_TYPE_METADATA,
  TITLE_TYPE_SELECT_OPTIONS,
  normalizeTitleType,
} from "../utils/titleTypeMetadata";
import { GeoLocationSelect, type GeoSelection } from "../../../components/geo/GeoLocationSelect";
import { useBillingSummary } from "../../../features/billing/hooks/useBillingSummary";
import { InfoBanner } from "../../../components/InfoBanner";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { blurWhen } from "../../shared/ui/stateStyles";

type BoundarySegmentDraft = {
  fromCorner: string;
  toCorner: string;
  bearing: string;
  distance: string;
};

type TieSegmentDraft = {
  toCorner: string;
  bearing: string;
  distance: string;
};

const createDefaultBoundarySegments = (): BoundarySegmentDraft[] => [
  { fromCorner: "1", toCorner: "2", bearing: "", distance: "" },
];

const normalizeCornerLabel = (value: string): string =>
  value.trim().toUpperCase();

const guessNextCornerLabel = (label: string): string => {
  const normalized = normalizeCornerLabel(label);
  if (!normalized) {
    return "";
  }
  const digitMatch = normalized.match(/^(\d+)([A-Z]?)$/);
  if (!digitMatch) {
    return "";
  }
  const baseNumber = parseInt(digitMatch[1], 10);
  if (Number.isNaN(baseNumber)) {
    return "";
  }
  const suffix = digitMatch[2];
  if (!suffix) {
    return String(baseNumber + 1);
  }
  const nextCharCode = suffix.charCodeAt(0) + 1;
  if (nextCharCode > 90) {
    return `${baseNumber + 1}A`;
  }
  return `${baseNumber}${String.fromCharCode(nextCharCode)}`;
};

const createDefaultTieSegment = (): TieSegmentDraft => ({

  toCorner: "1",

  bearing: "",

  distance: "",

});



const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";

const headerClasses = "flex flex-col gap-4";

const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col gap-6 dark:border-slate-800 dark:bg-slate-900";

const formStackClasses = "flex flex-col gap-8";

const gridClasses = "grid grid-cols-1 gap-6 sm:grid-cols-2";

const fieldClasses = "flex flex-col gap-2";

const inputClasses =

  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";

const hintClasses = "text-xs text-slate-500 dark:text-slate-400";
const introTextClasses = "mb-4 text-sm text-slate-600 dark:text-slate-400";
const errorBannerClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const signupStatusGridClasses =
  "grid w-full gap-3 text-sm text-slate-700 sm:grid-cols-2 dark:text-slate-200";
const signupStatusItemClasses =
  "flex items-center gap-3 rounded-xl bg-white/80 px-3.5 py-2.5 shadow-sm ring-1 ring-slate-100 transition hover:-translate-y-0.5 hover:ring-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500 dark:bg-slate-900/60 dark:ring-slate-700 dark:hover:ring-amber-700";
const signupStatusLabelClasses =
  "flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400";
const signupStatusValueClasses = "text-sm font-semibold";

const actionsClasses = "flex items-center justify-end gap-3 py-4 border-t border-slate-200 mt-4 dark:border-slate-800";
const actionsStackClasses = "flex flex-wrap items-center gap-3";

const tableClasses = "w-full text-left text-sm text-slate-600 border-collapse dark:text-slate-400";

const tableHeadClasses = "bg-slate-50 text-xs font-semibold uppercase text-slate-500 p-3 border-b border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700";

const tableCellClasses = "p-3 border-b border-slate-100 align-top dark:border-slate-800";



const ghostButtonClasses = "pm-btn pm-btn-ghost";

const primaryButtonClasses = "pm-btn pm-btn-primary";

const secondaryButtonClasses = "pm-btn pm-btn-surface";

const dangerButtonClasses = "pm-btn pm-btn-danger";



const DEV_TEMPLATE_LOT = {
  name: "Ponderosa",
  titleType: "TCT" as LotTitleType,
  titleNo: "XXX",
  lotNumber: "",
  planNumber: "",
  statedAreaM2: "6310",
  tiePointId: "c92f0430-686a-4cc4-884e-d74ebdcf026a",
  tieSegment: {
    toCorner: "1",
    bearing: "S. 74° 34' W.",
    distance: "4552.5",
  },
  boundaries: [
    { fromCorner: "1", toCorner: "2", bearing: "N. 09° 03' W.", distance: "43.86" },
    { fromCorner: "2", toCorner: "3", bearing: "N. 14° 14' W.", distance: "14.28" },
    { fromCorner: "3", toCorner: "4", bearing: "N. 14° 11' W.", distance: "57.07" },
    { fromCorner: "4", toCorner: "5", bearing: "N. 07° 47' W.", distance: "32.38" },
    { fromCorner: "5", toCorner: "6", bearing: "N. 84° 17' E.", distance: "65.63" },
    { fromCorner: "6", toCorner: "7", bearing: "S. 07° 47' E.", distance: "24.3" },
    { fromCorner: "7", toCorner: "8", bearing: "S. 00° 41' W.", distance: "14.51" },
    { fromCorner: "8", toCorner: "9", bearing: "S. 11° 06' W.", distance: "15.77" },
    { fromCorner: "9", toCorner: "10", bearing: "N. 85° 29' E.", distance: "26.33" },
    { fromCorner: "10", toCorner: "11", bearing: "S. 11° 06' W.", distance: "3.11" },
    { fromCorner: "11", toCorner: "12", bearing: "S. 85° 29' W.", distance: "25.49" },
    { fromCorner: "12", toCorner: "13", bearing: "S. 13° 30' W.", distance: "16.28" },
    { fromCorner: "13", toCorner: "14", bearing: "S. 17° 02' W.", distance: "16.74" },
    { fromCorner: "14", toCorner: "15", bearing: "S. 28° 28' W.", distance: "18.82" },
    { fromCorner: "15", toCorner: "16", bearing: "S. 23° 59' W.", distance: "17.47" },
    { fromCorner: "16", toCorner: "17", bearing: "S. 12° 32' W.", distance: "16.36" },
    { fromCorner: "17", toCorner: "18", bearing: "S. 12° 32' W.", distance: "28.3" },
    { fromCorner: "18", toCorner: "1", bearing: "N. 09° 25' W.", distance: "13.19" },
  ],
  geo: {
    regionId: "0db5482c-a33a-4afe-acb3-8c506cc18bca",
    provinceId: "01d0b822-d3f3-47dc-baab-4a01efd18bb9",
    municipalityId: "b2d246d4-6d6c-4b9c-8f1b-dcafb386589e",
    barangayId: "60997047-a14e-4b95-8cc2-0f0defde28eb",
  },
};

export function LotFormPage() {
  const { t } = useTranslation();
  const { workspaceId, lotId } = useParams<{
    workspaceId: string;
    lotId?: string;
  }>();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const location = useLocation();
  const workspace = (location.state as { workspace?: Workspace } | undefined)
    ?.workspace;
  const workspaceLinkState = useMemo(
    () => (workspace ? { workspace } : undefined),
    [workspace],
  );

  const isEditing = Boolean(lotId);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [controlPoints, setControlPoints] = useState<ControlPoint[]>([]);
  const [originalLot, setOriginalLot] = useState<Lot | null>(null);

  const [lotName, setLotName] = useState("");
  const [titleType, setTitleType] = useState<LotTitleType>(DEFAULT_TITLE_TYPE);
  const [titleNo, setTitleNo] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [planNumber, setPlanNumber] = useState("");
  const [statedArea, setStatedArea] = useState("");
  const [selectedTiePointId, setSelectedTiePointId] = useState<string>("");
  const [tieSegment, setTieSegment] = useState<TieSegmentDraft>(
    createDefaultTieSegment,
  );
  const [boundarySegments, setBoundarySegments] = useState<
    BoundarySegmentDraft[]
  >(() => createDefaultBoundarySegments());
  const [geoSelection, setGeoSelection] = useState<GeoSelection>({
    regionId: "",
    provinceId: "",
    municipalityId: "",
    barangayId: "",
  });
  const billingSummary = useBillingSummary();
  const lotCreditCost = billingSummary.data?.lot_creation_cost_credits ?? null;
  const availableCredits = billingSummary.data?.balance_credits ?? null;
  const hasVerifiedEmail = Boolean(user?.email_verified);
  const hasVerifiedPhone = Boolean(user?.phone_verified);
  const [creditErrorDetails, setCreditErrorDetails] = useState<{
    required: number;
    available: number;
  } | null>(null);
  const [documentDraftToken] = useState<string | null>(null);
  const isDevMode = import.meta.env.DEV;
  const hasSignupBonusUnlocked = Boolean(hasVerifiedEmail && hasVerifiedPhone);

  useEffect(() => {
    if (isEditing || billingSummary.isLoading) {
      return;
    }
    if (
      lotCreditCost != null &&
      availableCredits != null &&
      availableCredits < lotCreditCost
    ) {
      setCreditErrorDetails({
        required: lotCreditCost,
        available: availableCredits,
      });
    } else if (
      lotCreditCost != null &&
      availableCredits != null &&
      availableCredits >= lotCreditCost
    ) {
      setCreditErrorDetails(null);
    }
  }, [isEditing, billingSummary.isLoading, lotCreditCost, availableCredits]);

  const tiePointOptions = useMemo(() => {
    return controlPoints.map((point) => ({
      value: point.id,
      label: point.code + (point.name ? ` — ${point.name}` : ""),
    }));
  }, [controlPoints]);

  const hasTiePoints = tiePointOptions.length > 0;

  const pageTitle = isEditing ? t("workspaces.lot_form.edit_title") : t("workspaces.lot_form.add_title");
  const pageSubtitle = isEditing
    ? t("workspaces.lot_form.edit_subtitle")
    : t("workspaces.lot_form.add_subtitle");

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      { label: workspace?.name ?? t("workspaces.lots.title") },
      {
        label: t("workspaces.lots.title"),
        to: workspaceId ? `/workspaces/${workspaceId}/lots` : undefined,
        state: workspaceLinkState,
      },
      { label: pageTitle },
    ];
  }, [pageTitle, workspace, workspaceId, workspaceLinkState, t]);

  const formatFieldName = (field: string): string => {
    const lastSegment = field.split(".").pop() ?? field;
    return lastSegment
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderFieldErrors = () => {
    if (Object.keys(fieldErrors).length === 0) {
      return null;
    }
    return (
      <div className={errorBannerClasses}>
        <ul>
          {Object.entries(fieldErrors).map(([field, message]) => (
            <li key={field}>
              <strong>{formatFieldName(field)}</strong>: {message}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback:
      workspaceId != null
        ? { to: `/workspaces/${workspaceId}/lots`, state: workspaceLinkState }
        : undefined,
  });
  useEffect(() => {
    if (!accessToken || !workspaceId) {
      return;
    }
    let cancelled = false;

    const initialize = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const controlPointList = await listControlPoints(accessToken);
        if (cancelled) return;
        setControlPoints(controlPointList);
        if (controlPointList.length === 0) {
          setError(t("workspaces.lot_form.tie_required"));
        }

        if (isEditing && lotId) {
          const lot = await getLot(accessToken, lotId);
          if (cancelled) return;
          applyLotToForm(lot);
        } else {
          setOriginalLot(null);
          setLotName("");
          setTitleType(DEFAULT_TITLE_TYPE);
          setTitleNo("");
          setLotNumber("");
          setPlanNumber("");
          setStatedArea("");
          setTieSegment(createDefaultTieSegment());
          setBoundarySegments(createDefaultBoundarySegments());
          setGeoSelection({
            regionId: "",
            provinceId: "",
            municipalityId: "",
            barangayId: "",
          });
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError ? err.message : t("workspaces.lot_form.loading");
        setError(message);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void initialize();

    return () => {
      cancelled = true;
    };
  }, [accessToken, workspaceId, isEditing, lotId, t]);

  function applyLotToForm(lot: Lot) {
    setLotName(lot.name);
    setTitleType(normalizeTitleType(lot.title_type));
    setTitleNo(lot.title_no ?? "");
    setLotNumber(lot.lot_number ?? "");
    setPlanNumber(lot.plan_number ?? "");
    setStatedArea(
      typeof lot.stated_area_m2 === "number"
        ? lot.stated_area_m2.toString()
        : "",
    );
    if (lot.tie_point_id) {
      setSelectedTiePointId(lot.tie_point_id);
    }
    if (lot.segments && lot.segments.length > 0) {
      const tie = lot.segments.find((segment) => segment.is_tie);
      setTieSegment({
        toCorner: tie?.to_corner ?? "",
        bearing: tie?.bearing ?? "",
        distance:
          tie && typeof tie.distance_m === "number"
            ? tie.distance_m.toString()
            : "",
      });
      const boundaries = lot.segments
        .filter((segment) => !segment.is_tie)
        .map((segment) => ({
          fromCorner: segment.from_corner || "",
          toCorner: segment.to_corner || "",
          bearing: segment.bearing,
          distance:
            typeof segment.distance_m === "number"
              ? segment.distance_m.toString()
              : "",
        }));
      setBoundarySegments(
        boundaries.length > 0 ? boundaries : createDefaultBoundarySegments(),
      );
    } else {
      setTieSegment(createDefaultTieSegment());
      setBoundarySegments(createDefaultBoundarySegments());
    }
    setGeoSelection({
      regionId: lot.region_id ?? "",
      provinceId: lot.province_id ?? "",
      municipalityId: lot.municipality_id ?? "",
      barangayId: lot.barangay_id ?? "",
    });
    setOriginalLot(lot);
  }

  function applyTemplate(template: typeof DEV_TEMPLATE_LOT) {
    setLotName(template.name);
    setTitleType(template.titleType);
    setTitleNo(template.titleNo);
    setLotNumber(template.lotNumber);
    setPlanNumber(template.planNumber);
    setStatedArea(template.statedAreaM2);
    setSelectedTiePointId(template.tiePointId);
    setTieSegment(template.tieSegment);
    setBoundarySegments(template.boundaries);
    setGeoSelection(template.geo);
  }

  useEffect(() => {
    if (!isEditing && tiePointOptions.length > 0 && !selectedTiePointId) {
      setSelectedTiePointId(tiePointOptions[0].value);
    }
  }, [isEditing, tiePointOptions, selectedTiePointId]);

  useEffect(() => {
    if (error === t("workspaces.lot_form.tie_required") && selectedTiePointId) {
      setError(null);
    }
  }, [error, selectedTiePointId, t]);

  useEffect(() => {
    if (error === t("workspaces.lot_form.name_required") && lotName.trim()) {
      setError(null);
    }
  }, [error, lotName, t]);

  const addBoundarySegment = () => {
    setBoundarySegments((prev) => {
      const last = prev[prev.length - 1];
      const suggestedFrom = last?.toCorner?.trim()
        ? last.toCorner.trim()
        : last?.fromCorner?.trim() ?? "";
      const suggestedTo = guessNextCornerLabel(last?.toCorner ?? "");
      return [
        ...prev,
        {
          fromCorner: suggestedFrom,
          toCorner: suggestedTo,
          bearing: "",
          distance: "",
        },
      ];
    });
  };

  const removeBoundarySegment = (index: number) => {
    setBoundarySegments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateBoundarySegment = (
    index: number,
    field: keyof BoundarySegmentDraft,
    value: string,
  ) => {
    setBoundarySegments((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return next;
    });
  };

  const composeSegmentsPayload = (): LotSegmentInput[] | null => {
    const segments: LotSegmentInput[] = [];

    const tieDistance = parseFloat(tieSegment.distance);
    if (
      !tieSegment.bearing.trim() ||
      Number.isNaN(tieDistance) ||
      tieDistance <= 0
    ) {
      setError(t("workspaces.lot_form.tie_segment_invalid"));
      return null;
    }

    segments.push({
      seq_no: 0,
      from_corner: "TIE POINT",
      to_corner: tieSegment.toCorner || "1",
      bearing: tieSegment.bearing.trim(),
      distance_m: tieDistance,
      is_tie: true,
    });

    boundarySegments.forEach((segment, index) => {
      if (
        !segment.fromCorner.trim() ||
        !segment.toCorner.trim() ||
        !segment.bearing.trim()
      ) {
        return;
      }
      const distanceValue = parseFloat(segment.distance);
      if (Number.isNaN(distanceValue) || distanceValue <= 0) {
        return;
      }
      segments.push({
        seq_no: index + 1,
        from_corner: segment.fromCorner.trim(),
        to_corner: segment.toCorner.trim(),
        bearing: segment.bearing.trim(),
        distance_m: distanceValue,
        is_tie: false,
      });
    });

    if (segments.length < 2) {
      setError(t("workspaces.lot_form.boundary_required"));
      return null;
    }

    return segments;
  };

  const composeInputText = (segments: LotSegmentInput[]): string => {
    return segments
      .map((segment) => {
        const label = segment.is_tie
          ? `TO CORNER ${segment.to_corner}`
          : `${segment.from_corner}-${segment.to_corner}`;
        return `${label} ${segment.bearing} ${segment.distance_m.toFixed(2)} M.`;
      })
      .join("\n");
  };

  const handleArchive = async () => {
    if (!isEditing || !accessToken || !workspaceId || !lotId) {
      return;
    }
    if (originalLot?.archived_at) {
      return;
    }

    try {
      setIsArchiving(true);
      setError(null);
      setFieldErrors({});
      await archiveLot(accessToken, lotId);
      navigate(`/workspaces/${workspaceId}/lots/${lotId}`, {
        replace: true,
        state: workspaceLinkState,
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.lot_form.archive_error");
      setFieldErrors(err instanceof ApiError ? getValidationErrors(err) : {});
      setError(message);
    } finally {
      setIsArchiving(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken || !workspaceId) {
      return;
    }

    if (!hasTiePoints || !selectedTiePointId) {
      setError(t("workspaces.lot_form.tie_required"));
      return;
    }

    const trimmedName = lotName.trim();
    if (!trimmedName) {
      const message = t("workspaces.lot_form.name_required");
      setFieldErrors({ name: message });
      setError(message);
      return;
    }

    const segments = composeSegmentsPayload();
    if (!segments) {
      return;
    }

    if (!geoSelection.regionId || !geoSelection.provinceId) {
      const message = t("workspaces.lot_form.location_required");
      setFieldErrors({ location: message });
      setError(message);
      return;
    }

    const normalizedTitleNo = titleNo.trim();
    const normalizedLotNumber = lotNumber.trim();
    const normalizedPlanNumber = planNumber.trim();

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      if (isEditing && lotId) {
        const payload: UpdateLotPayload = {
          name: trimmedName,
          title_type: titleType,
          title_no: normalizedTitleNo || undefined,
          lot_number: normalizedLotNumber || undefined,
          plan_number: normalizedPlanNumber || undefined,
          tie_point_id: selectedTiePointId,
          stated_area_m2: statedArea ? parseFloat(statedArea) : undefined,
          segments,
          input_text: composeInputText(segments),
          region_id: geoSelection.regionId,
          province_id: geoSelection.provinceId,
          municipality_id: geoSelection.municipalityId || undefined,
          barangay_id: geoSelection.barangayId || undefined,
        };
        const updated = await updateLot(accessToken, lotId, payload);
        navigate(`/workspaces/${workspaceId}/lots/${updated.id}`);
      } else {
        const payload: CreateLotPayload = {
          workspace_id: workspaceId,
          name: trimmedName,
          title_type: titleType,
          title_no: normalizedTitleNo || undefined,
          lot_number: normalizedLotNumber || undefined,
          plan_number: normalizedPlanNumber || undefined,
          tie_point_id: selectedTiePointId,
          stated_area_m2: statedArea ? parseFloat(statedArea) : undefined,
          segments,
          input_text: composeInputText(segments),
          region_id: geoSelection.regionId,
          province_id: geoSelection.provinceId,
          municipality_id: geoSelection.municipalityId || undefined,
          barangay_id: geoSelection.barangayId || undefined,
          document_draft_token: documentDraftToken || undefined,
        };
        const created = await createLot(accessToken, payload);
        navigate(`/workspaces/${workspaceId}/lots/${created.id}`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setFieldErrors(getValidationErrors(err));
        if (!isEditing && err.status === 402) {
          const required = Number(
            err.body?.details?.required_credits ?? lotCreditCost ?? 0,
          );
          const available = Number(
            err.body?.details?.available_credits ?? availableCredits ?? 0,
          );
          setCreditErrorDetails({
            required,
            available,
          });
          setError(
            err.body?.message ??
              t("workspaces.lot_form.insufficient_credits.description", {
                required,
                available: Math.max(available, 0),
              }),
          );
          setIsSubmitting(false);
          return;
        }
        const message =
          err.body?.message ??
          (isEditing
            ? t("workspaces.lot_form.update_error")
            : t("workspaces.lot_form.create_error"));
        setError(message);
      } else {
        setFieldErrors({});
        setError(
          isEditing
            ? t("workspaces.lot_form.update_error")
            : t("workspaces.lot_form.create_error"),
        );
      }
      setIsSubmitting(false);
    }
  };

  const handleDevAutofill = () => {
    if (!isDevMode || isEditing) {
      return;
    }
    setError(null);
    setFieldErrors({});
    applyTemplate(DEV_TEMPLATE_LOT);
    setOriginalLot(null);
  };

  const handleCancel = () => {
    handleBack();
  };

  if (isLoading) {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          className={headerClasses}
          title={pageTitle}
          subtitle={pageSubtitle}
        />
        <p>{t("workspaces.lot_form.loading")}</p>
      </PageShell>
    );
  }

  if (error && isEditing && !isLoading) {
    return (
      <PageShell className={pageStackClasses} width="wide">
        <Breadcrumbs items={breadcrumbItems} />
        <PageHeader
          className={headerClasses}
          title={pageTitle}
          subtitle={pageSubtitle}
          actions={
            <div className="page-actions">
              <button type="button" className={ghostButtonClasses} onClick={handleCancel}>
                <ArrowLeft {...defaultIconProps} aria-hidden />
                {t("actions.back")}
              </button>
            </div>
          }
        />
        <p className={errorBannerClasses}>{error}</p>
        {renderFieldErrors()}
      </PageShell>
    );
  }

  const disableFormFields =
    !isEditing &&
    creditErrorDetails != null &&
    creditErrorDetails.available < creditErrorDetails.required;
  const disableSubmit = isSubmitting || disableFormFields;
  const canMoveLot =
    Boolean(
      isEditing &&
        lotId &&
        workspaceId &&
        workspace?.role === "owner" &&
        !originalLot?.archived_at,
    );

  return (
    <PageShell className={pageStackClasses} width="wide">
      <Breadcrumbs items={breadcrumbItems} />

      <PageHeader
        className={headerClasses}
        title={pageTitle}
        subtitle={pageSubtitle}
        actions={
          <div className={actionsStackClasses}>
            {!isEditing && isDevMode ? (
              <button
                type="button"
                className={secondaryButtonClasses}
                onClick={handleDevAutofill}
                disabled={isSubmitting}
              >
                <Sparkle {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_form.autofill_button")}
              </button>
            ) : null}
            {isEditing && !originalLot?.archived_at ? (
              <button
                type="button"
                className={dangerButtonClasses}
                onClick={() => setShowArchiveConfirm(true)}
                disabled={isArchiving}
              >
                <Archive {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_form.archive_button")}
              </button>
            ) : null}
            {canMoveLot ? (
              <Link
                className={secondaryButtonClasses}
                to={`/workspaces/${workspaceId}/lots/${lotId}/move`}
                state={workspaceLinkState}
              >
                <ArrowsLeftRight {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_form.move_button")}
              </Link>
            ) : null}
            {isEditing ? (
              <Link
                className={secondaryButtonClasses}
                to={`/workspaces/${workspaceId}/lots/${lotId}/documents`}
                state={workspaceLinkState}
              >
                <FileText {...defaultIconProps} aria-hidden />
                {t("workspaces.lot_detail.documents_button")}{" "}
                {originalLot?.agg_documents_count != null ? `(${originalLot.agg_documents_count})` : ""}
              </Link>
            ) : null}
            <button
              type="button"
              className={ghostButtonClasses}
              onClick={handleBack}
              disabled={isArchiving}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {!hasSignupBonusUnlocked ? (
        <InfoBanner
          tone="info"
          prominent
          iconSize="lg"
          icon={<Gift {...defaultIconProps} size={28} aria-hidden />}
          title={t("dashboard.signup_bonus.locked_title")}
          description={
            <div className="space-y-3">
              <p>{t("workspaces.lot_form.signup_bonus.description")}</p>
              <div className={signupStatusGridClasses}>
                {[
                  {
                    key: "email",
                    label: t("forms.email.label"),
                    value: hasVerifiedEmail
                      ? t("dashboard.signup_bonus.status_verified")
                      : t("dashboard.signup_bonus.status_pending"),
                    to: "/settings/profile#profile-email",
                    icon: hasVerifiedEmail ? (
                      <CheckCircle
                        {...defaultIconProps}
                        weight="fill"
                        className="text-emerald-600"
                        aria-hidden
                      />
                    ) : (
                      <Clock
                        {...defaultIconProps}
                        className="text-amber-600"
                        aria-hidden
                      />
                    ),
                    toneClass: hasVerifiedEmail
                      ? "text-emerald-700 dark:text-emerald-200"
                      : "text-slate-600 dark:text-slate-400",
                  },
                  {
                    key: "phone",
                    label: t("forms.phone.label"),
                    value: hasVerifiedPhone
                      ? t("dashboard.signup_bonus.status_verified")
                      : t("dashboard.signup_bonus.status_pending"),
                    to: "/settings/profile#profile-phone",
                    icon: hasVerifiedPhone ? (
                      <CheckCircle
                        {...defaultIconProps}
                        weight="fill"
                        className="text-emerald-600"
                        aria-hidden
                      />
                    ) : (
                      <Clock
                        {...defaultIconProps}
                        className="text-amber-600"
                        aria-hidden
                      />
                    ),
                    toneClass: hasVerifiedPhone
                      ? "text-emerald-700 dark:text-emerald-200"
                      : "text-amber-700 dark:text-amber-200",
                  },
                ].map((item) => (
                  <Link
                    key={item.key}
                    to={item.to}
                    className={signupStatusItemClasses}
                    aria-label={item.label}
                  >
                    {item.icon}
                    <div className="flex flex-col">
                      <span className={signupStatusLabelClasses}>{item.label}</span>
                      <span className={`${signupStatusValueClasses} ${item.toneClass}`}>
                        {item.value}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          }
        />
      ) : null}

      {!isEditing && creditErrorDetails ? (
        <InfoBanner
            tone="warning"
          prominent
          icon={<Wallet {...defaultIconProps} aria-hidden />}
            iconSize="lg"
          title={t("workspaces.lot_form.insufficient_credits.title")}
          description={t("workspaces.lot_form.insufficient_credits.description", {
            required: creditErrorDetails.required,
            available: Math.max(creditErrorDetails.available, 0),
          })}
        >
          <div className="flex flex-col gap-3">
            <Link className={primaryButtonClasses + " self-start"} to="/billing">
              {t("workspaces.lot_form.insufficient_credits.cta")}
            </Link>
          </div>
        </InfoBanner>
      ) : null}

      {error && <div className={errorBannerClasses}>{error}</div>}
      {renderFieldErrors()}

      <form
        className={[formStackClasses, blurWhen(disableFormFields)].filter(Boolean).join(" ")}
        onSubmit={handleSubmit}
      >
        <fieldset className="contents" disabled={disableFormFields}>
        <section className={cardClasses}>
          <h2>{t("workspaces.lot_form.title_details")}</h2>
          <div className={gridClasses}>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.lot_name_label")} />
              <input
                type="text"
                value={lotName}
                onChange={(event) => setLotName(event.target.value)}
                placeholder={t("workspaces.lot_form.lot_name_placeholder")}
                maxLength={255}
                required
                className={inputClasses}
              />
              <small className={hintClasses}>
                {t("workspaces.lot_form.lot_name_hint")}
              </small>
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.title_type_label")} />
              <select
                value={titleType}
                onChange={(event) =>
                  setTitleType(event.target.value as LotTitleType)
                }
                className={inputClasses}
              >
                {TITLE_TYPE_SELECT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small className={hintClasses}>
                {TITLE_TYPE_METADATA[titleType].description}
              </small>
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.title_number_label")} optional />
              <input
                type="text"
                value={titleNo}
                onChange={(event) => setTitleNo(event.target.value)}
                placeholder={t("workspaces.lot_form.title_number_placeholder")}
                maxLength={255}
                className={inputClasses}
              />
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.lot_number_label")} optional />
              <input
                type="text"
                value={lotNumber}
                onChange={(event) => setLotNumber(event.target.value)}
                placeholder={t("workspaces.lot_form.lot_number_placeholder")}
                maxLength={255}
                className={inputClasses}
              />
              <small className={hintClasses}>
                {t("workspaces.lot_form.lot_number_hint")}
              </small>
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.plan_number_label")} optional />
              <input
                type="text"
                value={planNumber}
                onChange={(event) => setPlanNumber(event.target.value)}
                placeholder={t("workspaces.lot_form.plan_number_placeholder")}
                maxLength={255}
                className={inputClasses}
              />
              <small className={hintClasses}>
                {t("workspaces.lot_form.plan_number_hint")}
              </small>
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.stated_area_label")} optional />
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={statedArea}
                onChange={(event) => setStatedArea(event.target.value)}
                placeholder={t("workspaces.lot_form.stated_area_placeholder")}
                className={inputClasses}
              />
            </label>
          </div>
        </section>

        <section className={cardClasses}>
          <h2>{t("workspaces.lot_form.location_title")}</h2>
          <p className={introTextClasses}>
            {t("workspaces.lot_form.location_description")}
          </p>
          <GeoLocationSelect
            value={geoSelection}
            onChange={setGeoSelection}
            includeBarangay
            required
          />
        </section>

            <SectionWithHeader title={t("workspaces.lot_form.tie_segment_title")} description="TODO" />

        <section className={cardClasses}>
          <h2>{t("workspaces.lot_form.tie_segment_title")}</h2>
          <div className={gridClasses}>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.tie_point_label")} />
              <select
                value={selectedTiePointId}
                onChange={(event) => {
                  setSelectedTiePointId(event.target.value);
                  if (error === t("workspaces.lot_form.tie_required")) {
                    setError(null);
                  }
                }}
                required
                disabled={!hasTiePoints}
                className={inputClasses}
              >
                {tiePointOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {!hasTiePoints && (
                <small className={hintClasses}>
                  {t("workspaces.lot_form.tie_point_empty")}
                </small>
              )}
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.tie_bearing_label")} />
              <input
                type="text"
                value={tieSegment.bearing}
                onChange={(event) =>
                  setTieSegment((prev) => ({
                    ...prev,
                    bearing: event.target.value,
                  }))
                }
                placeholder={t("workspaces.lot_form.tie_bearing_placeholder")}
                required
                className={inputClasses}
              />
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.tie_distance_label")} />
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={tieSegment.distance}
                onChange={(event) =>
                  setTieSegment((prev) => ({
                    ...prev,
                    distance: event.target.value,
                  }))
                }
                required
                className={inputClasses}
              />
            </label>
            <label className={fieldClasses}>
              <FormFieldLabel label={t("workspaces.lot_form.tie_corner_label")} />
              <input
                type="text"
                value={tieSegment.toCorner}
                onChange={(event) =>
                  setTieSegment((prev) => ({
                    ...prev,
                    toCorner: event.target.value,
                  }))
                }
                placeholder={t("workspaces.lot_form.tie_corner_placeholder")}
                required
                className={inputClasses}
              />
            </label>
          </div>
        </section>


        <section className={cardClasses}>
          <h2>{t("workspaces.lot_form.boundary_title")}</h2>
          <table className={tableClasses}>
            <thead>
              <tr>
                <th className={tableHeadClasses}>{t("workspaces.lot_form.table_from")}</th>
                <th className={tableHeadClasses}>{t("workspaces.lot_form.table_to")}</th>
                <th className={tableHeadClasses}>{t("workspaces.lot_form.table_bearing")}</th>
                <th className={tableHeadClasses}>{t("workspaces.lot_form.table_distance")}</th>
                <th className={tableHeadClasses} />
              </tr>
            </thead>
            <tbody>
              {boundarySegments.map((segment, index) => (
                <tr key={index}>
                  <td className={tableCellClasses}>
                    <input
                      type="text"
                      value={segment.fromCorner}
                      onChange={(event) =>
                        updateBoundarySegment(
                          index,
                          "fromCorner",
                          event.target.value,
                        )
                      }
                      placeholder={t("workspaces.lot_form.from_placeholder")}
                      required
                      className={inputClasses}
                    />
                  </td>
                  <td className={tableCellClasses}>
                    <input
                      type="text"
                      value={segment.toCorner}
                      onChange={(event) =>
                        updateBoundarySegment(
                          index,
                          "toCorner",
                          event.target.value,
                        )
                      }
                      placeholder={t("workspaces.lot_form.to_placeholder")}
                      required
                      className={inputClasses}
                    />
                  </td>
                  <td className={tableCellClasses}>
                    <input
                      type="text"
                      value={segment.bearing}
                      onChange={(event) =>
                        updateBoundarySegment(
                          index,
                          "bearing",
                          event.target.value,
                        )
                      }
                      placeholder={t("workspaces.lot_form.bearing_placeholder")}
                      required
                      className={inputClasses}
                    />
                  </td>
                  <td className={tableCellClasses}>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={segment.distance}
                      onChange={(event) =>
                        updateBoundarySegment(
                          index,
                          "distance",
                          event.target.value,
                        )
                      }
                      required
                      className={inputClasses}
                    />
                  </td>
                  <td className={tableCellClasses}>
                    <button
                      type="button"
                      className={ghostButtonClasses}
                      onClick={() => removeBoundarySegment(index)}
                      disabled={boundarySegments.length <= 1}
                    >
                      <TrashSimple {...defaultIconProps} aria-hidden />
                      {t("workspaces.lot_form.remove_button")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            className={secondaryButtonClasses}
            onClick={addBoundarySegment}
          >
          <Plus {...defaultIconProps} aria-hidden />
          {t("workspaces.lot_form.add_segment_button")}
        </button>
      </section>

        </fieldset>
        <div className={actionsClasses}>
          <button
            type="button"
          className={ghostButtonClasses}
          onClick={handleCancel}
          disabled={isSubmitting || disableFormFields}
        >
          <ArrowLeft {...defaultIconProps} aria-hidden />
          {t("actions.cancel")}
        </button>
          <button
            type="submit"
            className={primaryButtonClasses}
            disabled={disableSubmit || !hasTiePoints || isArchiving}
          >
            {isSubmitting ? (
              t("workspaces.lot_form.saving")
            ) : (
              <>
                <FloppyDisk {...defaultIconProps} aria-hidden />
                {isEditing ? t("workspaces.lot_form.save_button") : t("workspaces.lot_form.create_button")}
              </>
            )}
          </button>
        </div>
      </form>
      <ConfirmDialog
        open={showArchiveConfirm}
        title={t("workspaces.lot_form.archive_confirm")}
        confirmLabel={t("workspaces.lot_form.archive_button")}
        cancelLabel={t("actions.cancel")}
        confirmTone="danger"
        confirmLoading={isArchiving}
        onCancel={() => setShowArchiveConfirm(false)}
        onConfirm={async () => {
          await handleArchive();
          setShowArchiveConfirm(false);
        }}
      />
    </PageShell>
  );
}
